import { useState, type ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { leetcodeUrl, topicName } from "../../data/prep/topics";
import type { PlanItem } from "../../utils/prepPlanner";
import type { PracticeResult } from "../../utils/prepSkills";
import type { PrepItemState } from "../../utils/prep";
import { DebriefDialog, type DebriefProps } from "./PrepDebrief";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";

// One plan item's popover: open it, say how it went and how long it took,
// skip it, or undo. Used by the week calendar and the Today card.
//
// "Open in LeetCode" starts a timer (this browser only); the result tap
// offers the time since then. With no timer, or a time that can't be real,
// it asks with chips instead.

const TIMER_KEY = (id: string) => `prep:timer:${id}`;
/** Longer than this since opening: they left the tab open, so ask */
const MAX_TIMED = 120;
const TIME_CHIPS: { label: string; minutes: number }[] = [
  { label: "Under 15", minutes: 10 },
  { label: "15–30", minutes: 22 },
  { label: "30–45", minutes: 38 },
  { label: "45+", minutes: 55 },
];

const RESULTS: { value: PracticeResult; label: string }[] = [
  { value: "solved", label: "Solved" },
  { value: "hints", label: "With hints" },
  { value: "missed", label: "Didn’t get it" },
];
export const RESULT_LABEL: Record<PracticeResult, string> = { solved: "Solved", hints: "Solved with hints", missed: "Didn’t get it" };

function startTimer(id: string) {
  try {
    localStorage.setItem(TIMER_KEY(id), String(Date.now()));
  } catch {
    // storage unavailable: the result tap asks with chips
  }
}

function timedMinutes(id: string): number | null {
  try {
    const started = Number(localStorage.getItem(TIMER_KEY(id)));
    if (!started) return null;
    const minutes = Math.max(1, Math.round((Date.now() - started) / 60000));
    return minutes <= MAX_TIMED ? minutes : null;
  } catch {
    return null;
  }
}

function clearTimer(id: string) {
  try {
    localStorage.removeItem(TIMER_KEY(id));
  } catch {
    // nothing to clear
  }
}

/** "Medium · Arrays & Hashing · 35 min", or "Finish the React course · 30 min" */
export function itemMeta(item: PlanItem, hideTopics = false): string {
  const topic = item.topic && item.kind === "problem" && !hideTopics ? topicName(item.topic) : null;
  return [item.difficulty ?? item.goal, topic, `${item.minutes} min`].filter(Boolean).join(" · ");
}

/**
 * An item's "Why" line. With topics hidden, a problem's says why without
 * naming its topic, or nothing (also for one planned before that line existed).
 */
export function shownReason(item: PlanItem, hideTopics = false): string {
  if (!hideTopics || !item.topic || item.kind === "story") return item.reason;
  return item.blindReason ?? "";
}

export function OpenLink({ item, className }: { item: PlanItem; className?: string }) {
  if (!item.slug) return null;
  return (
    <a
      href={leetcodeUrl(item.slug)}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        startTimer(item.id);
      }}
      className={cn("inline-flex items-center gap-0.5 underline decoration-melt-text3/40 underline-offset-2 hover:decoration-melt-text1", className)}
    >
      Open in LeetCode
      <ArrowUpRight size={12} strokeWidth={1.75} />
    </a>
  );
}

function ItemBody({
  item,
  state,
  onDone,
  onSkip,
  onUndo,
  hideTopics,
  close,
}: {
  item: PlanItem;
  hideTopics?: boolean;
  state?: PrepItemState;
  onDone: (result?: PracticeResult, minutes?: number) => void;
  /** back: "Not now", it comes back later; otherwise skipped for good */
  onSkip: (back: boolean) => void;
  onUndo: () => void;
  close: () => void;
}) {
  const timed = timedMinutes(item.id);
  const [result, setResult] = useState<PracticeResult | null>(null);
  const [minutes, setMinutes] = useState<number | null>(timed);
  const status = state?.status;

  const finish = (r?: PracticeResult, m?: number | null) => {
    onDone(r, m ?? undefined);
    clearTimer(item.id);
    close();
  };

  const quiet =
    "rounded-[9px] px-2.5 py-1.5 text-[13px] font-medium text-melt-text2 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1";
  const choice = (on: boolean) =>
    cn(
      "flex-1 whitespace-nowrap rounded-[8px] border border-melt-hair bg-white px-1.5 py-1 text-[12px] font-medium text-melt-text2 transition-colors hover:border-melt-text1/25 hover:text-melt-text1",
      on && "border-melt-caramel bg-melt-peach text-melt-text1"
    );

  return (
    <div className="flex w-[300px] flex-col gap-3 p-4 font-grotesk text-melt-text1">
      <div>
        <p className="text-[15px] font-semibold leading-snug">{item.title}</p>
        <p className="mt-0.5 font-plex text-[11.5px] text-melt-text3">{itemMeta(item, hideTopics)}</p>
      </div>
      {shownReason(item, hideTopics) && <p className="text-[13px] leading-[1.5] text-melt-text2">{shownReason(item, hideTopics)}</p>}

      {status ? (
        <div className="flex items-center justify-between rounded-[10px] bg-melt-track/70 px-3 py-2">
          <span className="text-[13px] text-melt-text2">
            {status === "skipped"
              ? "Skipped, it won’t come back"
              : status === "later"
              ? "Not now, it’ll come back in a few days"
              : [state?.result ? RESULT_LABEL[state.result] : "Done", state?.minutes ? `${state.minutes} min` : null]
                  .filter(Boolean)
                  .join(" · ")}
          </span>
          <button
            type="button"
            className={quiet}
            onClick={() => {
              onUndo();
              close();
            }}
          >
            Undo
          </button>
        </div>
      ) : item.kind === "problem" ? (
        <>
          <OpenLink item={item} className="self-start text-[13px] font-medium text-melt-text1" />
          <div className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold">How did it go?</span>
            <div className="flex gap-1.5">
              {RESULTS.map(({ value, label }) => (
                <button key={value} type="button" className={choice(result === value)} onClick={() => setResult(value)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {result && (
            <div className="flex flex-col gap-1.5 duration-200 animate-in fade-in">
              {timed !== null ? (
                <label className="flex items-center gap-2 text-[13px] text-melt-text2">
                  Took
                  <input
                    type="number"
                    min={1}
                    max={240}
                    value={minutes ?? ""}
                    onChange={(e) => setMinutes(e.target.value ? Number(e.target.value) : null)}
                    className="h-8 w-16 rounded-[8px] border border-melt-hair bg-white px-2 text-center font-plex text-[13px] text-melt-text1 outline-none focus:border-melt-caramel"
                  />
                  min?
                </label>
              ) : (
                <>
                  <span className="text-[12.5px] font-semibold">How long did it take?</span>
                  <div className="flex gap-1.5">
                    {TIME_CHIPS.map((chip) => (
                      <button key={chip.label} type="button" className={choice(minutes === chip.minutes)} onClick={() => setMinutes(chip.minutes)}>
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={() => finish(result, minutes)}
                className="mt-1 h-9 rounded-[10px] bg-melt-caramel text-[13.5px] font-medium text-white transition-colors hover:bg-melt-press"
              >
                Save
              </button>
            </div>
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={() => finish()}
          className="h-9 rounded-[10px] bg-melt-caramel text-[13.5px] font-medium text-white transition-colors hover:bg-melt-press"
        >
          Mark done
        </button>
      )}

      {!status && (
        <div className="flex items-center gap-4">
          <button
            type="button"
            className={cn(quiet, "px-0 hover:bg-transparent")}
            title="Take it off today; it comes back in a few days"
            onClick={() => {
              onSkip(true);
              close();
            }}
          >
            Not now
          </button>
          {item.kind === "problem" && (
            <button
              type="button"
              className={cn(quiet, "px-0 hover:bg-transparent")}
              title="Never plan this problem again"
              onClick={() => {
                onSkip(false);
                close();
              }}
            >
              Skip this problem
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export type { DebriefProps } from "./PrepDebrief";

export default function PrepItemPopover({
  item,
  state,
  children,
  align = "start",
  onDone,
  onSkip,
  onUndo,
  debrief,
  hideTopics,
}: {
  item: PlanItem;
  state?: PrepItemState;
  children: ReactNode;
  align?: "start" | "center" | "end";
  onDone: (result?: PracticeResult, minutes?: number) => void;
  /** back: "Not now", it comes back later; otherwise skipped for good */
  onSkip: (back: boolean) => void;
  onUndo: () => void;
  debrief?: DebriefProps;
  /** Leave problems' topics out (the user's setting) */
  hideTopics?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // A debrief not written yet opens its dialog; once it's done, the popover shows Done and Undo
  if (item.kind === "debrief" && debrief && !state?.status) {
    return <DebriefDialog open={open} onOpenChange={setOpen} trigger={children} debrief={debrief} />;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="border-melt-hair bg-melt-menu p-0 shadow-[0_18px_40px_-18px_rgba(92,52,23,.4)]">
        {open && (
          <ItemBody
            item={item}
            state={state}
            onDone={onDone}
            onSkip={onSkip}
            onUndo={onUndo}
            hideTopics={hideTopics}
            close={() => setOpen(false)}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
